import React from 'react';
import { useAuth } from '../contexts/AuthContext';

const DashboardPage = () => {
  const { user } = useAuth();

  const quickStats = [
    { label: 'Workouts This Week', value: '0', icon: '💪', color: 'primary' },
    { label: 'Total Exercises', value: '0', icon: '🏃‍♂️', color: 'success' },
    { label: 'Goals Achieved', value: '0', icon: '🎯', color: 'warning' },
    { label: 'Days Active', value: '0', icon: '📅', color: 'info' },
  ];

  const recentActivities = [
    { type: 'welcome', message: 'Welcome to MB Fitness!', time: 'Just now', icon: '🎉' },
    { type: 'setup', message: 'Complete your profile to get started', time: '1 min ago', icon: '⚙️' },
  ];

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <h1 className="display-5 fw-bold text-dark mb-2">
                Welcome back, {user?.username}! 👋
              </h1>
              <p className="lead text-muted">
                Ready to crush your fitness goals today?
              </p>
            </div>
            <div className="text-end">
              <small className="text-muted">
                {new Date().toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </small>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="row mb-4">
        <div className="col-12">
          <h2 className="h4 mb-3">Quick Stats</h2>
        </div>
        {quickStats.map((stat, index) => (
          <div key={index} className="col-lg-3 col-md-6 mb-3">
            <div className={`card border-0 shadow-sm h-100 border-start border-${stat.color} border-4`}>
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="me-3">
                    <span style={{ fontSize: '2rem' }}>{stat.icon}</span>
                  </div>
                  <div>
                    <h3 className={`h2 fw-bold text-${stat.color} mb-0`}>{stat.value}</h3>
                    <p className="text-muted mb-0 small">{stat.label}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="row">
        {/* Profile Section */}
        <div className="col-lg-4 mb-4">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header bg-white border-0 pb-0">
              <h3 className="h5 mb-0">Your Profile</h3>
            </div>
            <div className="card-body text-center">
              <div 
                className="rounded-circle bg-primary text-white d-inline-flex align-items-center justify-content-center mb-3"
                style={{ width: '80px', height: '80px', fontSize: '2rem', fontWeight: 'bold' }}
              >
                {user?.username?.charAt(0).toUpperCase()}
              </div>
              <h4 className="h5 mb-2">{user?.username}</h4>
              <p className="text-muted mb-3">{user?.mail}</p>
              
              <div className="row text-start">
                <div className="col-12 mb-2">
                  <div className="d-flex justify-content-between">
                    <small className="text-muted">Member since:</small>
                    <small className="fw-medium">
                      {new Date(user?.created_at).toLocaleDateString()}
                    </small>
                  </div>
                </div>
                <div className="col-12">
                  <div className="d-flex justify-content-between">
                    <small className="text-muted">Last updated:</small>
                    <small className="fw-medium">
                      {new Date(user?.updated_at).toLocaleDateString()}
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="col-lg-4 mb-4">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header bg-white border-0 pb-0">
              <h3 className="h5 mb-0">Recent Activity</h3>
            </div>
            <div className="card-body p-0">
              <div className="list-group list-group-flush">
                {recentActivities.map((activity, index) => (
                  <div key={index} className="list-group-item border-0 py-3">
                    <div className="d-flex align-items-center">
                      <span className="me-3" style={{ fontSize: '1.5rem' }}>
                        {activity.icon}
                      </span>
                      <div className="flex-grow-1">
                        <p className="mb-1">{activity.message}</p>
                        <small className="text-muted">{activity.time}</small>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="col-lg-4 mb-4">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header bg-white border-0 pb-0">
              <h3 className="h5 mb-0">Quick Actions</h3>
            </div>
            <div className="card-body">
              <div className="row g-2">
                <div className="col-6">
                  <button className="btn btn-outline-primary w-100 py-3" disabled>
                    <div className="d-flex flex-column align-items-center">
                      <span style={{ fontSize: '1.5rem' }}>🏋️‍♂️</span>
                      <small className="mt-1">Start Workout</small>
                    </div>
                  </button>
                </div>
                <div className="col-6">
                  <button className="btn btn-outline-success w-100 py-3" disabled>
                    <div className="d-flex flex-column align-items-center">
                      <span style={{ fontSize: '1.5rem' }}>📊</span>
                      <small className="mt-1">View Progress</small>
                    </div>
                  </button>
                </div>
                <div className="col-6">
                  <button className="btn btn-outline-warning w-100 py-3" disabled>
                    <div className="d-flex flex-column align-items-center">
                      <span style={{ fontSize: '1.5rem' }}>🥗</span>
                      <small className="mt-1">Log Nutrition</small>
                    </div>
                  </button>
                </div>
                <div className="col-6">
                  <button className="btn btn-outline-info w-100 py-3" disabled>
                    <div className="d-flex flex-column align-items-center">
                      <span style={{ fontSize: '1.5rem' }}>⚙️</span>
                      <small className="mt-1">Settings</small>
                    </div>
                  </button>
                </div>
              </div>
              <p className="text-center text-muted mt-3 mb-0">
                <small><em>These features are coming soon!</em></small>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
